<template>
  <div class="">
    <div v-if="isLiteSubscriber">
      <offer-builder-preview class="relative mt-16"></offer-builder-preview>
    </div>
    <div v-else-if="surveyStep && surveyStep.enabled" class="max-w-screen-xl mx-auto py-8 px-4 sm:py-2 sm:px-6 lg:px-8 lg:pb-10">
      <div
        v-if="showTemplateMessage"
        class="flex items-center space-x-3 px-4 py-2 mb-8 rounded font-medium bg-indigo-100 text-brand-blue max-w-3xl mx-auto"
      >
        <font-awesome-icon :icon="['fas', 'info-circle']" class="mr-2 mt-1" />
        <span class="flex-1 text-sm">
          Because you’re using a cancel flow template, we’ve already created these offers for you. They need to be completed before you push live.
        </span>
        <el-button type="text" size="medium" @click="showTemplateMessage = false"><span class="font-bold">Dismiss</span></el-button>
      </div>
      <div
        v-if="showAIMessage"
        class="flex items-center space-x-3 px-4 py-2 mb-8 rounded font-medium bg-indigo-100 text-brand-blue max-w-3xl mx-auto"
      >
        <font-awesome-icon :icon="['fas', 'info-circle']" class="mr-2 mt-1" />
        <span class="flex-1 text-sm"> These offers have been generated by our AI. Please review them before you push live. </span>
      </div>
      <div class="flex"></div>
      <div class="lg:grid lg:grid-cols-2 lg:gap-4 lg:gap-y-10">
        <div v-for="choice in survey.choices" :key="choice.guid" class="brand-card relative mt-4 lg:mt-1 mb-12 lg:mb-0">
          <offer-card-new
            :choice="choice"
            :surveyOffer="true"
            @rework="reworkSurveyChoice"
            @update="(update) => choiceOfferUpdate(choice.guid, update)"
            @create="(offerType) => createOffer(choice.guid, offerType)"
            @delete="deleteOffer(choice.guid)"
          />
        </div>
      </div>
    </div>
    <div v-else>
      <div class="container max-w-screen-md relative">
        <wavy-card>
          <div class="-mb-36">
            <div class="absolute left-0 right-0 top-0 flex justify-center">
              <font-awesome-layers class="-mt-8 text-6xl">
                <font-awesome-icon :icon="['fas', 'circle']" class="text-purple-400" />
                <font-awesome-icon :icon="['fas', 'question']" class="text-brand-black opacity-50 text-3xl" />
              </font-awesome-layers>
            </div>
            <div class="mt-8 text-center">
              <p class="font-bold text-center text-brand-black text-2xl">Add a cancellation survey</p>
              <p class="font-semibold text-brand-brown text-center text-lg mt-1">
                Offers are triggered dynamically from Cancellation Survey responses. Add questions in Flow Builder to customize your Offers.
              </p>
              <div class="mt-12">
                <el-button type="primary" class="text-lg font-semibold py-4" @click="$emit('back')">← Go to Flow Builder</el-button>
              </div>
            </div>
          </div>
        </wavy-card>
      </div>
    </div>
    <AIJobMonitor :survey="survey" />
  </div>
</template>

<script>
import OfferCardNew from '@/components/Builder/OfferCardNew.vue';
import OfferBuilderPreview from '@/components/Builder/OfferBuilderPreview.vue';
import WavyCard from '@/components/UI/WavyCard.vue';
import { createBaseOffer, OFFER_TYPE, STEP_TYPE } from '@/store/shared';
import { UPDATE_BLUEPRINT_SURVEY } from '@/store/mutation-types';
import { BLUEPRINT_SCHEDULE_SAVE, FETCH_ORG_COUPONS, FETCH_ORG_PRICES, AI_REWORK_SURVEY_CHOICE } from '@/store/action-types';
import AIJobMonitor from './AIJobMonitor.vue';

export default {
  name: 'OfferBuilder',
  components: {
    WavyCard,
    OfferCardNew,
    OfferBuilderPreview,
    AIJobMonitor,
  },
  data() {
    return {
      showTemplateMessage: !!this.$store.state.selectedTemplate,
    };
  },
  created() {
    this.loadCouponsAndPrices();
  },
  computed: {
    selectedTemplate() {
      return this.$store.state.selectedTemplate;
    },
    blueprint() {
      return this.$store.getters.currentBlueprint;
    },
    blueprintGuid() {
      return this.blueprint.guid;
    },
    showAIMessage() {
      return false;
      // return this.blueprint.aiGenerated;
    },
    stepMap() {
      return this.$store.getters.blueprintStepMap;
    },
    isLiteSubscriber() {
      return this.$store.getters.isLiteSubscriber;
    },
    surveyStep() {
      return this.stepMap[STEP_TYPE.SURVEY];
    },
    survey() {
      if (this.surveyStep) {
        return this.surveyStep.survey;
      }
      return null;
    },
    coupons() {
      return this.$store.getters.filteredCoupons;
    },
  },
  methods: {
    reworkSurveyChoice(choice) {
      let additionalData = {};
      // attach coupon information if applicable
      if (choice.offer && choice.offer.offerType === OFFER_TYPE.DISCOUNT) {
        const {
          discountConfig: { couponId },
        } = choice.offer;
        const coupon = this.coupons.find((c) => c.id === couponId);
        if (coupon) {
          additionalData = { couponData: coupon };
        }
      }
      this.$store.dispatch(AI_REWORK_SURVEY_CHOICE, {
        blueprintGuid: this.blueprintGuid,
        surveyChoiceGuid: choice.guid,
        surveyChoice: choice,
        additionalData,
      });
    },
    scheduleSave() {
      this.$store.dispatch(BLUEPRINT_SCHEDULE_SAVE, { id: this.blueprint._id });
    },
    surveyUpdate(update) {
      this.$store.commit(UPDATE_BLUEPRINT_SURVEY, {
        surveyGuid: this.survey.guid,
        data: update,
      });
      this.scheduleSave();
    },
    createOffer(choiceGuid, offerType) {
      // get new choices object
      const newOffer = createBaseOffer(offerType);
      this.choiceOfferSet(choiceGuid, newOffer);
    },
    deleteOffer(choiceGuid) {
      const choices = this.survey.choices.map((choice) => {
        if (choice.guid === choiceGuid) {
          delete choice.offer;
          return { ...choice };
        }
        return choice;
      });
      this.surveyUpdate({ choices });
    },
    // write new offer object
    choiceOfferSet(choiceGuid, offer) {
      // get new choices object
      const choices = this.survey.choices.map((choice) => {
        if (choice.guid === choiceGuid) {
          // create new choice object with updated offer superimposed on old offer
          return {
            ...choice,
            offer,
          };
        }
        return choice;
      });
      this.surveyUpdate({ choices });
    },
    // update fields without overwriting
    choiceOfferUpdate(choiceGuid, update) {
      // get new choices object
      const choices = this.survey.choices.map((choice) => {
        if (choice.guid === choiceGuid) {
          // create new choice object with updated offer superimposed on old offer
          return {
            ...choice,
            offer: {
              ...choice.offer,
              ...update,
            },
          };
        }
        return choice;
      });
      this.surveyUpdate({ choices });
    },
    loadCouponsAndPrices() {
      this.$store.dispatch(FETCH_ORG_COUPONS);
      this.$store.dispatch(FETCH_ORG_PRICES);
    },
  },
};
</script>
